import {useParams, useNavigate} from "react-router-dom";
import {useQuery} from "@tanstack/react-query";
import {useState, useEffect} from "react";
import {
    getWorkbookQuestions,
    getWorkbook,
    saveAnswer,
    checkAnswerWithAI,
    updateAiResponse
} from "../../../api/workbooks";
import {TextArea} from "../../elements/forms/Inputs";
import {PrimaryButton, SecondaryButton} from "../../elements/Buttons";
import ReactMarkdown from 'react-markdown';
import {trackButtonClick} from "../../../utils/gtm";

function AiResponsesDisplay({aiResponses, currentIndex, setCurrentIndex, onResponseUpdate, isLoading}) {
    const [showFeedback, setShowFeedback] = useState(false);
    const [feedbackText, setFeedbackText] = useState("");
    const [pendingRating, setPendingRating] = useState(null);
    const [isSubmittingFeedback, setIsSubmittingFeedback] = useState(false);
    const ratingEnabled = false;
    
    // Update feedback text when current response changes
    useEffect(() => {
        if (aiResponses && aiResponses[currentIndex]) {
            setFeedbackText(aiResponses[currentIndex].feedback || "");
            setShowFeedback(false);
            setPendingRating(null);
        }
    }, [aiResponses, currentIndex]);

    if (isLoading) {
        return (
            <div className="mt-6 p-3 border rounded bg-gray-50 text-sm">
                <div className="flex items-center space-x-2">
                    <span className="text-xs text-gray-600 font-medium">AI Feedback</span>
                </div>
                <div className="mt-2 flex items-center space-x-2 text-gray-600">
                    <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <span className="text-sm italic">AI thinking...</span>
                </div>
            </div>
        );
    }

    if (!aiResponses || aiResponses.length === 0) return null;

    const currentResponse = aiResponses[currentIndex];
    const hasMultiple = aiResponses.length > 1;

    const goToPrevious = () => {
        setCurrentIndex(prev => prev > 0 ? prev - 1 : aiResponses.length - 1);
    };

    const goToNext = () => {
        setCurrentIndex(prev => prev < aiResponses.length - 1 ? prev + 1 : 0);
    };

    const handleRating = (rating) => {
        setPendingRating(rating);
        setShowFeedback(true);
    };

    const handleFeedbackSubmit = async () => {
        try {
            setIsSubmittingFeedback(true);
            const updatedResponse = await updateAiResponse(currentResponse.id, {
                rating: pendingRating,
                feedback: feedbackText
            });
            onResponseUpdate(currentIndex, updatedResponse);
            setShowFeedback(false);
            setPendingRating(null);
        } catch (err) {
            console.error("Error submitting feedback:", err);
        } finally {
            setIsSubmittingFeedback(false);
        }
    };

    const handleFeedbackCancel = () => {
        setShowFeedback(false);
        setPendingRating(null);
        setFeedbackText(currentResponse.feedback || "");
    };

    return (
        <div className="mt-6 p-3 border rounded bg-gray-50 text-sm">
            <style>{`
                .ai-response-content ul {
                    list-style-type: disc;
                    margin-left: 1.5rem;
                    margin-top: 0.5rem;
                    margin-bottom: 0.5rem;
                }
                .ai-response-content ol {
                    list-style-type: decimal;
                    margin-left: 1.5rem;
                    margin-top: 0.5rem;
                    margin-bottom: 0.5rem;
                }
                .ai-response-content li {
                    margin-bottom: 0.25rem;
                }
                .ai-response-content p {
                    margin-bottom: 0.5rem;
                }
                .ai-response-content p:last-child {
                    margin-bottom: 0;
                }
            `}</style>
            <div className="flex justify-between items-start mb-2">
                <span className="text-xs text-gray-600 font-medium">AI Feedback</span>
                {hasMultiple && (
                    <div className="flex items-center space-x-2">
                        <button
                            onClick={goToPrevious}
                            className="text-xs px-2 py-1 bg-gray-200 hover:bg-gray-300 rounded transition-colors"
                            title="Previous suggestion"
                        >
                            ←
                        </button>
                        <span className="text-xs text-gray-600">
                            {currentIndex + 1} of {aiResponses.length}
                        </span>
                        <button
                            onClick={goToNext}
                            className="text-xs px-2 py-1 bg-gray-200 hover:bg-gray-300 rounded transition-colors"
                            title="Next suggestion"
                        >
                            →
                        </button>
                    </div>
                )}
            </div>
            <div className="ai-response-content">
                <ReactMarkdown>{currentResponse.llm_response}</ReactMarkdown>
            </div>

            {/* Rating buttons */}
            {ratingEnabled && <div className="mt-3 pt-3 border-t border-gray-200">
                <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                        <span className="text-xs text-gray-600 font-medium">Rate this suggestion:</span>
                        <button
                            onClick={() => handleRating(1)}
                            className={`p-1 rounded transition-colors ${
                                (currentResponse.rating === 1 || pendingRating === 1)
                                    ? 'bg-green-100 text-green-600'
                                    : 'text-gray-400 hover:text-green-600 hover:bg-green-50'
                            }`}
                            title="Thumbs up"
                        >
                            👍
                        </button>
                        <button
                            onClick={() => handleRating(0)}
                            className={`p-1 rounded transition-colors ${
                                (currentResponse.rating === 0 || pendingRating === 0)
                                    ? 'bg-red-100 text-red-600'
                                    : 'text-gray-400 hover:text-red-600 hover:bg-red-50'
                            }`}
                            title="Thumbs down"
                        >
                            👎
                        </button>
                    </div>
                </div>

                {/* Feedback area */}
                {showFeedback && (
                    <div className="mt-3">
                        <label className="block text-xs text-gray-600 font-medium mb-1">
                            Please provide feedback for your {pendingRating === 1 ? 'positive' : 'negative'} rating:
                        </label>
                        <textarea
                            value={feedbackText}
                            onChange={(e) => setFeedbackText(e.target.value)}
                            className="w-full p-2 text-sm border border-gray-300 rounded resize-none"
                            rows="3"
                            placeholder="Tell us more about your rating..."
                        />
                        <div className="flex justify-end space-x-2 mt-2">
                            <button
                                onClick={handleFeedbackCancel}
                                className="px-3 py-1 text-xs text-gray-600 hover:text-gray-800 transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleFeedbackSubmit}
                                disabled={isSubmittingFeedback}
                                className="px-3 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 transition-colors"
                            >
                                {isSubmittingFeedback ? 'Submitting...' : 'Submit Rating & Feedback'}
                            </button>
                        </div>
                    </div>
                )}
            </div>}

            {currentIndex > 0 && currentResponse.user_answer && (
                <div className="mt-3 pt-3 border-t border-gray-200">
                    <div className="text-xs text-gray-600 font-medium mb-1">Your answer was:</div>
                    <div className="text-sm text-gray-700 bg-gray-100 p-2 rounded">
                        {currentResponse.user_answer}
                    </div>
                </div>
            )}
        </div>
    );
}

function QuestionItem({question, workbookTitle}) {
    const [text, setText] = useState(question.answer ? question.answer.answer : "");
    const [answerId, setAnswerId] = useState(question.answer ? question.answer.id : null);
    const [aiResponses, setAiResponses] = useState(question.answer ? question.answer.ai_responses || [] : []);
    const [currentResponseIndex, setCurrentResponseIndex] = useState(0);
    const [error, setError] = useState("");
    const [isCheckingWithAI, setIsCheckingWithAI] = useState(false);
    const [isSaved, setIsSaved] = useState(true);
    const [lastSavedText, setLastSavedText] = useState(question.answer ? question.answer.answer : "");

    // Update state when question prop changes (e.g., when user changes)
    useEffect(() => {
        const answerText = question.answer ? question.answer.answer : "";
        setText(answerText);
        setAnswerId(question.answer ? question.answer.id : null);
        setAiResponses(question.answer ? question.answer.ai_responses || [] : []);
        setCurrentResponseIndex(0);
        setLastSavedText(answerText);
        // Only mark as saved if there's actually an answer
        setIsSaved(!!answerText);
    }, [question.id, question.answer]);

    const handleResponseUpdate = (index, updatedResponse) => {
        const newResponses = [...aiResponses];
        newResponses[index] = updatedResponse;
        setAiResponses(newResponses);
    };

    const handleTextChange = (e) => {
        setText(e.target.value);
        setIsSaved(false);
    };

    async function save() {
        try {
            setError("");
            const payload = {id: answerId, question: question, answer: text};
            const result = await saveAnswer(payload);
            setAnswerId(result.id);
            setLastSavedText(text);
            setIsSaved(true);
            if (result.ai_responses) {
                setAiResponses(result.ai_responses);
                setCurrentResponseIndex(0);
            }
        } catch (err) {
            console.error("Error saving answer:", err);
            setError("Failed to save answer. Please try again.");
        }
    }

    async function check() {
        try {
            setError("");
            setIsCheckingWithAI(true);

            // Track button click with GTM
            trackButtonClick("Check with AI", workbookTitle, {
                question_id: question.id,
                question_text: question.question
            });

            const payload = {id: answerId, question: question, answer: text};
            const result = await checkAnswerWithAI(payload);
            setAnswerId(result.id);
            if (result.ai_responses) {
                setAiResponses(result.ai_responses);
                setCurrentResponseIndex(0); // Show first response by default
            }
        } catch (err) {
            console.error("Error checking answer with AI:", err);
            setError("Failed to check answer with AI. Please try again.");
        } finally {
            setIsCheckingWithAI(false);
        }
    }

    return (
        <div className="mb-6">
            {question.pre_content && (
                <div className="mb-4 prose prose-sm max-w-none"
                     dangerouslySetInnerHTML={{__html: question.pre_content}}/>
            )}
            <p className="font-medium mb-2">{question.question}</p>
            {question.help_text && (
                <div className="mb-2 text-sm text-gray-700">
                    <ReactMarkdown>{question.help_text}</ReactMarkdown>
                </div>
            )}
            <TextArea path="answer"
                      rows={10}
                      data={{answer: text}} errors={{}} onChange={handleTextChange}
                      className=""/>
            {error && (
                <div className="mt-2 p-2 border rounded bg-red-50 text-red-700 text-sm">
                    {error}
                </div>
            )}
            <div className="mt-2 space-x-2">
                <PrimaryButton
                    onClick={save}
                    label={isSaved ? "Saved" : "Save"}
                    saved={isSaved}
                />
                <SecondaryButton onClick={check} label="Check with AI" loading={isCheckingWithAI}/>
            </div>
            <AiResponsesDisplay
                aiResponses={aiResponses}
                currentIndex={currentResponseIndex}
                setCurrentIndex={setCurrentResponseIndex}
                onResponseUpdate={handleResponseUpdate}
                isLoading={isCheckingWithAI}
            />
            {question.post_content && (
                <div className="mt-4 prose prose-sm max-w-none"
                     dangerouslySetInnerHTML={{__html: question.post_content}}/>
            )}
        </div>
    )
}

export default function WorkbookQuestions({workbookId = null}) {
    const params = useParams();
    const navigate = useNavigate();
    const id = workbookId ?? params.id;
    const {data: questions, isLoading: questionsLoading} = useQuery(["workbook_questions", id], () => getWorkbookQuestions(id));
    const {data: workbook, isLoading: workbookLoading} = useQuery(["workbook", id], () => getWorkbook(id));
    const [pageIndex, setPageIndex] = useState(0); // TODO can we use URL params via the main page through iframe?

    if (questionsLoading || workbookLoading) {
        return <div>Loading...</div>;
    }

    if (!questions || !workbook) return <></>;

    const pages = {};
    questions.forEach(q => {
        pages[q.page] = pages[q.page] || [];
        pages[q.page].push(q);
    });
    const pageNumbers = Object.keys(pages).sort((a, b) => a - b);
    const currentPage = pageNumbers[pageIndex];
    const currentQuestions = pages[currentPage];

    // Check if we're in an embed context
    const isEmbed = window.location.pathname.startsWith('/embed');

    const handleBackToList = () => {
        if (isEmbed) {
            // Preserve query params for embed (wp_token, tenant_id)
            const searchParams = window.location.search;
            navigate(`/embed/all_workbooks${searchParams}`);
        } else {
            navigate('/');
        }
    };

    const BackButton = () => (
        <a
            href="#"
            onClick={(e) => {
                e.preventDefault();
                handleBackToList();
            }}
            className="inline-flex items-center text-sm hover:underline"
        >
            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to Workbook List
        </a>
    );

    return (
        <div className="mx-auto max-w-3xl p-4">
            <style>{workbook.styles}</style>

            {/*<BackButton />*/}

            {pageIndex === 0 && (
                <>
                    <h1 className="text-2xl font-bold mb-2">{workbook.title}</h1>
                    {workbook.description && (
                        <div className="mb-4 text-gray-700" dangerouslySetInnerHTML={{__html: workbook.description}}>
                        </div>
                    )}
                </>
            )}
            {currentQuestions && currentQuestions.map(q => (
                <QuestionItem key={q.id} question={q} workbookTitle={workbook.title}/>
            ))}
            {pageNumbers.length > 1 && (
                <div className="flex justify-between mt-4">
                    <SecondaryButton disabled={pageIndex === 0} onClick={() => setPageIndex(pageIndex - 1)}
                                     label="Previous"></SecondaryButton>
                    <SecondaryButton disabled={pageIndex === pageNumbers.length - 1}
                                     onClick={() => setPageIndex(pageIndex + 1)} label="Next"></SecondaryButton>
                </div>
            )}

            {/*<div className="mt-6 pt-4 border-t border-gray-200">*/}
            {/*    <BackButton />*/}
            {/*</div>*/}
        </div>
    )
}
